/**
 * x-file-upload directive.
 *
 * Provides a drag-and-drop file upload area with file name display and
 * individual file removal. Supports both single and multiple file inputs.
 * When a file picker dialog is cancelled, the previous file selection is
 * restored automatically.
 *
 * Sub-directives:
 *   x-file-upload:input - Applied to the `<input type="file">` element.
 *                         Binds the `x-ref="fileInput"` reference and the
 *                         change handler.
 *
 * @example
 *   <div x-file-upload>
 *     <input x-file-upload:input type="file" multiple>
 *     <template x-for="(name, index) in fileNames" :key="index">
 *       <span x-text="name"></span>
 *       <button @click="removeFile(index)">Remove</button>
 *     </template>
 *   </div>
 */
(() => {
  /**
   * Binds the file `<input>` element with the `fileInput` ref and a change
   * listener that delegates to the root's `handleFileSelect`.
   *
   * @param {object} Alpine - The Alpine.js instance.
   * @param {HTMLInputElement} el - The file input element.
   */
  const input = (Alpine, el) => {
    Alpine.bind(el, () => ({
      ["x-ref"]: "fileInput",

      ["@change"](event) {
        this.handleFileSelect(event);
      },
    }));
  };

  const root = (Alpine, el) => {
    Alpine.bind(el, () => ({
      ["@drop.prevent"](event) {
        this.handleDrop(event);
      },

      ["@dragover.prevent"]() {
        this.dragOver = true;
      },

      ["@dragleave.prevent"]() {
        this.dragOver = false;
      },

      ["x-data"]() {
        let previousFiles = null;

        return {
          fileNames: [],
          dragOver: false,

          /**
           * Handles a drop event by assigning the dropped files to the file
           * input. For a `multiple` input all dropped files are added; for a
           * single-file input only the first file is used.
           * @param {DragEvent} event - The native drop event.
           */
          handleDrop(event) {
            this.dragOver = false;
            const files = event.dataTransfer.files;

            if (files.length > 0) {
              // Create a new DataTransfer object to set files on the input.
              const dataTransfer = new DataTransfer();

              if (this.$refs.fileInput.multiple) {
                // Add all dropped files for a multiple file input.
                for (let i = 0; i < files.length; i++) {
                  dataTransfer.items.add(files[i]);
                }
              } else {
                // Add only the first file for a single file input.
                dataTransfer.items.add(files[0]);
              }

              // Set the files on the input element.
              this.$refs.fileInput.files = dataTransfer.files;

              // Update file names for display.
              this.fileNames = Array.from(files).map((file) => file.name);

              // Trigger change event on the input.
              this.$refs.fileInput.dispatchEvent(
                new Event("change", { bubbles: true }),
              );
            }
          },

          /**
           * Handles the file input's change event. Updates `fileNames` when
           * new files are selected. When the file picker is cancelled (empty
           * file list), the previous file selection is restored.
           * @param {Event} event - The native change event.
           */
          handleFileSelect(event) {
            const files = event.target.files;

            if (files.length > 0) {
              this.fileNames = Array.from(files).map((file) => file.name);

              // Store for tracking.
              previousFiles = this.$refs.fileInput.files;
            } else {
              if (previousFiles.length) {
                // File picker was cancelled — restore previous files.
                this.fileNames = Array.from(previousFiles).map(
                  (file) => file.name,
                );

                this.$refs.fileInput.files = previousFiles;
              }
            }
          },

          /**
           * Removes the file at the given index from the input and updates the
           * displayed file names. Dispatches a change event after removal.
           * @param {number} index - The zero-based index of the file to remove.
           */
          removeFile(index) {
            const dataTransfer = new DataTransfer();
            const files = Array.from(this.$refs.fileInput.files);

            // Add all files except the one being removed.
            files.forEach((file, i) => {
              if (i !== index) {
                dataTransfer.items.add(file);
              }
            });

            // Update the input's files.
            this.$refs.fileInput.files = dataTransfer.files;

            // Update previous files before triggering change.
            previousFiles = dataTransfer.files;

            // Update file names.
            this.fileNames = Array.from(dataTransfer.files).map(
              (file) => file.name,
            );

            // Trigger change event.
            this.$refs.fileInput.dispatchEvent(
              new Event("change", { bubbles: true }),
            );
          },

          /**
           * Returns true when at least one file has been selected.
           * @returns {boolean}
           */
          get hasFiles() {
            return this.fileNames.length > 0;
          },
        };
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive("file-upload", (el, { value }) => {
      if ("input" === value) {
        input(Alpine, el);
      } else {
        root(Alpine, el);
      }
    });
  });
})();
